import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  LayoutDashboard, RotateCcw, ShoppingBag, Phone, MessageCircle, Truck, Receipt,
  LineChart, Package, Sparkles, Megaphone, LifeBuoy, MessageSquare, Mail,
  Settings, Users, ScrollText, Search, Bell, LogOut, ChevronDown, Menu, X,
  Wrench, AlertOctagon, Boxes, EyeOff, Calculator, PackageCheck, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import LiveIndicator from "@/components/layout/LiveIndicator";
import NotificationBell from "@/components/layout/NotificationBell";

const ICONS = {
  LayoutDashboard, RotateCcw, ShoppingBag, Phone, MessageCircle, Truck, Receipt,
  LineChart, Package, Sparkles, Megaphone, LifeBuoy, MessageSquare, Mail,
  Settings, Users, ScrollText, Wrench, AlertOctagon, Boxes, Calculator, PackageCheck, Send,
};

export default function DashboardLayout() {
  const { user, logout, isPreviewing, previewUser, exitPreview, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    api.get("/modules").then((r) => setModules(r.data)).catch(() => {});
  }, []);

  // COD queue pending count for the Livrări sidebar badge. Refreshed on
  // mount + every 5 min. Cheap: single aggregate under 30ms.
  const [queueBadge, setQueueBadge] = useState(null);
  const [expediereBadge, setExpediereBadge] = useState(null);
  useEffect(() => {
    let stopped = false;
    const load = () => {
      api.get("/delivery/confirmation-queue/stats")
        .then((r) => { if (!stopped) setQueueBadge(r.data); })
        .catch(() => {});
      api.get("/expediere/sidebar-badge")
        .then((r) => { if (!stopped) setExpediereBadge(r.data); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 5 * 60_000);
    return () => { stopped = true; clearInterval(t); };
  }, []);

  // When previewing as another user, hide modules they don't have permission for.
  // Also permanently hide the deprecated WhatsApp + Call Center modules.
  const HIDDEN_MODULE_IDS = new Set(["whatsapp", "callcenter"]);
  const visibleModules = (modules || []).filter((m) => {
    if (HIDDEN_MODULE_IDS.has(m.id)) return false;
    if (m.path === "/whatsapp" || m.path === "/call-center") return false;
    if (!isPreviewing) return true;
    if (!m.permission) return true;
    const perms = previewUser?.permissions || [];
    return perms.includes(m.permission);
  });

  const initials = (user?.name || user?.email || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  const navItem = (to, icon, label, opts = {}) => {
    const Icon = ICONS[icon] || LayoutDashboard;
    return (
      <NavLink
        key={to}
        to={to}
        end={opts.end}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
            opts.disabled
              ? "text-muted-foreground/60 cursor-not-allowed"
              : isActive
              ? "bg-primary/10 text-primary font-semibold"
              : "text-muted-foreground hover:bg-primary/5 hover:text-foreground"
          }`
        }
        onClick={(e) => {
          if (opts.disabled) e.preventDefault();
          setSidebarOpen(false);
        }}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        {opts.badge && opts.badge > 0 && (
          <Badge
            variant="secondary"
            className={`text-[10px] px-1.5 py-0 h-5 min-w-[20px] justify-center ${opts.badgeClass || ""}`}
            data-testid={`nav-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {opts.badge > 99 ? "99+" : opts.badge}
          </Badge>
        )}
        {opts.disabled && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-border text-muted-foreground/70">
            Curând
          </Badge>
        )}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {isPreviewing && (
        <div className="sticky top-0 z-50 bg-amber-100 border-b border-amber-300 text-amber-900 text-xs font-medium flex items-center justify-center gap-3 h-8 px-4" data-testid="preview-bar">
          <EyeOff className="h-3.5 w-3.5" />
          Preview ca <strong>{previewUser?.name}</strong> ({previewUser?.role}) — UI-ul reflectă permisiunile lor. Acțiunile rulează în continuare ca <strong>{user?.email}</strong>.
          <button
            onClick={() => { exitPreview(); navigate("/setari/utilizatori"); }}
            className="ml-2 underline hover:no-underline"
            data-testid="exit-preview-btn"
          >
            Ieși din preview
          </button>
        </div>
      )}
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-64 border-r border-border bg-white transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-5 bg-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <img
              src="/ieftinit-logo.png"
              alt="IEFTINIT.RO"
              className="h-9 w-auto object-contain shrink-0"
              data-testid="brand-logo"
            />
            <div className="leading-none min-w-0">
              <div className="font-display font-bold text-[15px] tracking-tight">Control Hub</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium">IEFTINIT.RO</div>
            </div>
          </div>
          <button className="lg:hidden text-muted-foreground" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col h-[calc(100vh-4rem)] overflow-y-auto scrollbar-thin px-3 py-4 gap-1">
          {navItem("/", "LayoutDashboard", "Overview", { end: true })}

          <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold px-3 mt-5 mb-2">
            Module
          </div>
          {visibleModules.map((m) => {
            const isDelivery =
              m.path === "/livrari" || m.id === "delivery" || m.icon === "Truck";
            const isExpediere =
              m.path === "/expediere" || m.id === "expediere";
            const pending = queueBadge?.pending_total || 0;
            const highRisk = queueBadge?.pending_high || 0;
            const wait24h = expediereBadge?.waiting_over_24h || 0;
            let badgeOpts = {};
            if (isDelivery && pending > 0) {
              badgeOpts = {
                badge: pending,
                badgeClass:
                  highRisk > 0
                    ? "bg-rose-100 text-rose-700 border-rose-200"
                    : (queueBadge?.pending_medium || 0) > 0
                    ? "bg-amber-100 text-amber-800 border-amber-200"
                    : "bg-emerald-100 text-emerald-700 border-emerald-200",
              };
            } else if (isExpediere && wait24h > 0) {
              badgeOpts = {
                badge: wait24h,
                badgeClass: "bg-rose-100 text-rose-700 border-rose-200",
              };
            }
            return m.enabled
              ? navItem(m.path, m.icon, m.name, badgeOpts)
              : navItem(`/modul/${m.id}`, m.icon, m.name, { disabled: true });
          })}

          <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold px-3 mt-5 mb-2">
            Setări
          </div>
          {hasPermission("returns.manage_settings") && navItem("/setari/statusuri", "Settings", "Statusuri retur")}
          {hasPermission("service.manage_settings") && navItem("/setari/service-statusuri", "Wrench", "Statusuri service")}
          {hasPermission("service.manage_settings") && navItem("/setari/tipuri-probleme", "AlertOctagon", "Tipuri probleme")}
          {hasPermission("delivery.settings") && navItem("/setari/livrari", "Truck", "GLS / Livrări")}
          {hasPermission("shopify.manage") && navItem("/setari/shopify", "ShoppingBag", "Shopify")}
          {hasPermission("billing.view") && navItem("/setari/facturare", "Receipt", "Facturare · SmartBill")}
          {hasPermission("users.view") && navItem("/setari/utilizatori", "Users", "Utilizatori")}
          {hasPermission("audit.view") && navItem("/setari/audit", "ScrollText", "Audit Log")}

          <div className="mt-auto px-3 pt-4 pb-2 border-t border-border">
            <div className="text-[10px] text-muted-foreground/80 leading-relaxed">
              v0.1.0 · MVP<br />© IEFTINIT.RO
            </div>
          </div>
        </nav>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 glass h-16 flex items-center justify-between gap-4 px-4 sm:px-6 bg-white/85" data-testid="topbar">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button className="lg:hidden text-muted-foreground" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </button>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Caută retururi, comenzi, clienți…"
                className="pl-9 h-9 bg-secondary/50 border-border focus:bg-background"
                data-testid="global-search"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <LiveIndicator />
            <NotificationBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-secondary transition-colors" data-testid="user-menu">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden md:block">
                    <div className="text-xs font-medium leading-tight">{user?.name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{user?.role}</div>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/setari/statusuri")} data-testid="menu-settings">
                  <Settings className="h-4 w-4 mr-2" />
                  Setări
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" />
                  Delogare
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
